import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldStar, UserPlus, Trash, Warning, Envelope, Users } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';

export default function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [players, setPlayers] = useState([]);
  const [mode, setMode] = useState("email"); // 'email' | 'player'
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [playerEmail, setPlayerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [aRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/admins`, { withCredentials: true }),
        axios.get(`${API}/players?include_external=false`),
      ]);
      setAdmins(aRes.data || []);
      setPlayers(pRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load admins");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addByEmail = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/admins`,
        { email: email.trim(), name: name.trim() || null },
        { withCredentials: true });
      toast.success(res.data.message);
      setEmail(""); setName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add admin");
    } finally { setSubmitting(false); }
  };

  const addByPlayer = async () => {
    if (!selectedPlayerId) { toast.error("Select a player first"); return; }
    if (!playerEmail.trim() || !playerEmail.includes("@")) {
      toast.error("Enter the player's email"); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/admins`,
        { email: playerEmail.trim(), player_id: selectedPlayerId },
        { withCredentials: true });
      toast.success(res.data.message);
      setSelectedPlayerId(""); setPlayerEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add admin");
    } finally { setSubmitting(false); }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await axios.delete(`${API}/admin/admins/${revokeTarget.user_id}`, { withCredentials: true });
      toast.success(`Revoked admin from ${revokeTarget.email}`);
      setRevokeTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke");
      setRevokeTarget(null);
    }
  };

  const unclaimedPlayers = players.filter(p => !p.claimed_by_user_id);

  return (
    <Card className="bg-card border-border" data-testid="admin-management-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldStar className="w-5 h-5 text-secondary" weight="fill" />
          Admin Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Admins List */}
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" />Current Admins ({admins.length})
          </h3>
          <div className="space-y-2">
            {admins.map(a => (
              <div
                key={a.user_id}
                className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20"
                data-testid={`admin-row-${a.user_id}`}
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={a.picture} alt={a.name} />
                    <AvatarFallback className="text-xs bg-muted">{getInitials(a.name || a.email)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {a.name || a.email}
                      {a.seeded && !a.last_login_at && (
                        <Badge variant="outline" className="text-[9px] border-secondary/40 text-secondary">Pending first login</Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {a.email}{a.claimed_player_name ? ` · ${a.claimed_player_name}` : ""}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10"
                  onClick={() => setRevokeTarget(a)}
                  data-testid={`revoke-admin-${a.user_id}`}
                >
                  <Trash className="w-4 h-4 mr-1" />Revoke
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Add Admin */}
        <div className="border-t border-border pt-4 space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <UserPlus className="w-4 h-4" />Add Admin
          </h3>
          <div className="flex gap-2">
            <Button
              variant={mode === "email" ? "default" : "outline"}
              size="sm"
              onClick={() => setMode("email")}
              data-testid="admin-add-mode-email"
            ><Envelope className="w-4 h-4 mr-1" />By Email</Button>
            <Button
              variant={mode === "player" ? "default" : "outline"}
              size="sm"
              onClick={() => setMode("player")}
              data-testid="admin-add-mode-player"
            ><Users className="w-4 h-4 mr-1" />From Player</Button>
          </div>

          {mode === "email" ? (
            <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-xs text-muted-foreground">
                Enter the user's Google email. They'll get admin access the moment they sign in.
                If they already have an account, their existing profile will be promoted.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email <span className="text-destructive">*</span></Label>
                  <Input
                    type="email" placeholder="admin@gmail.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    data-testid="admin-add-email-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Display Name (optional)</Label>
                  <Input
                    placeholder="Full name"
                    value={name} onChange={e => setName(e.target.value)}
                    data-testid="admin-add-name-input"
                  />
                </div>
              </div>
              <Button onClick={addByEmail} disabled={submitting} data-testid="admin-add-by-email-btn">
                <ShieldStar className="w-4 h-4 mr-1" />Grant Admin
              </Button>
            </div>
          ) : (
            <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-xs text-muted-foreground">
                Select an existing (unclaimed) player, then enter their email. The admin role will be granted AND
                the player profile will be linked to that email in one step.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Player <span className="text-destructive">*</span></Label>
                  <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                    <SelectTrigger data-testid="admin-add-player-select">
                      <SelectValue placeholder={unclaimedPlayers.length === 0 ? "No unclaimed players" : "Select player"} />
                    </SelectTrigger>
                    <SelectContent>
                      {unclaimedPlayers.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} · {p.tier}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Email <span className="text-destructive">*</span></Label>
                  <Input
                    type="email" placeholder="player@gmail.com"
                    value={playerEmail} onChange={e => setPlayerEmail(e.target.value)}
                    data-testid="admin-add-player-email-input"
                  />
                </div>
              </div>
              <Button onClick={addByPlayer} disabled={submitting || !selectedPlayerId} data-testid="admin-add-by-player-btn">
                <ShieldStar className="w-4 h-4 mr-1" />Link &amp; Grant Admin
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      {/* Revoke confirmation */}
      <AlertDialog open={!!revokeTarget} onOpenChange={open => !open && setRevokeTarget(null)}>
        <AlertDialogContent data-testid="revoke-admin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning className="w-5 h-5 text-destructive" weight="fill" />
              Revoke admin from {revokeTarget?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This user will lose all admin access immediately. Their player claim (if any) is preserved.
              You can re-grant admin at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-revoke-admin-btn"
            >
              <Trash className="w-4 h-4 mr-2" />Yes, revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
